import React, { useEffect, useMemo } from 'react';
import './flightCard2.less';
import './flightCard2.mobile.less';
import { FaExclamationCircle } from 'react-icons/fa';
import { Col, Row, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  Flight,
  PriceDetails,
  FlightType,
} from '../../../../interfaces/flightInterface';
import FlightButton2 from '../../../../components/FlightButton/FlightButton2';
import FlightOutLineSection from './FlightOutLineSection/FlightOutLineSection';
import { Product } from '../../../ShoppingCart/interfaces';
import AgentCommissionSearch from '../../../../components/AgentCommissionSearch/AgentCommissionSearch';
import { selectSearchParamsPaxCount } from '../../searchDatePickerSlice';
import { SkyInventoryBadge } from '@skygroup/shared/components/sky-inventory-badge/SkyInventoryBadge';
import { TenantTsType } from '@skygroup/shared/constants/tenantTs';
import { getTsTypeByIdentifier } from '@skygroup/shared/utils/flightIdentifier';
import { AirlineLogoWithToolTip } from './AirlineLogoWithTooltip';
import { UrlPaxCount } from '../../../../hooks/useCoreSearchParams';
import { RealCabin, fromWireCabin, highestCabin } from '@skygroup/shared/utils/cabinClass';


const buildFlightJointName = (airlineId: any, flightNumber: string): string => {
  const id = String(airlineId ?? '');
  const num = String(flightNumber ?? '');
  const stripped = num.startsWith(id) ? num.slice(id.length) : num;
  return `${id}-${stripped}`;
};

/**
 * Collapses one leg's segments into a single cabin to show on the leg row.
 * Looks across ALL segments (not just firstEl, unlike the rest of
 * modifyFlightData) because a leg's segments can legitimately disagree.
 *
 * `mixed: true` covers TWO distinct situations, both meaning "don't claim a
 * single cabin with full confidence":
 *  1. Segments reported different real cabins — legitimate for Amadeus
 *     multi-city, whose cabin filter uses `coverage: 'MOST_SEGMENTS'` rather
 *     than all-segments.
 *  2. Some segments reported a cabin and others didn't (partial data) — if
 *     any provider ever populates `cabin` unevenly within one leg, showing
 *     the one known cabin with full confidence would overclaim what the
 *     leg as a whole actually delivered. We still show what we know, but
 *     flag it exactly like a genuine disagreement, rather than asserting a
 *     single cabin for segments we have no data on.
 *
 * Returns null only when NO segment reports a real cabin — the case for
 * every ALP/SKY360 offer today, whose segments carry `cabin: null` (an
 * on-type value — `Flight.cabin`/`FlightView.cabin` are typed
 * `… | null`) or omit the key entirely (which IS off-type; real data
 * arrives via loosely-typed axios responses, so the required key isn't
 * actually enforced at runtime). Callers must treat null as "render
 * nothing", never as an empty/placeholder tag.
 */
const summarizeCabin = (
  segments: any[]
): { cabin: RealCabin; mixed: boolean } | null => {
  const knownCabins = segments
    .map((segment) => segment?.cabin)
    .filter((wire): wire is string => typeof wire === 'string' && wire.length > 0)
    .map((wire) => fromWireCabin(wire))
    // fromWireCabin now returns undefined for an unrecognised value rather than
    // the old 'all' sentinel, so this is plain truthiness.
    .filter((option): option is RealCabin => !!option);

  if (knownCabins.length === 0) {
    return null;
  }

  const distinct = Array.from(new Set(knownCabins));
  const hasUnknownSegment = knownCabins.length < segments.length;

  return {
    cabin: highestCabin(distinct),
    mixed: distinct.length > 1 || hasUnknownSegment,
  };
};

export const modifyFlightData = (data: any, isFlightBack?: boolean) => {
  if (!data) {
    return undefined;
  }

  const isFlightsSegments = Array.isArray(data);
  const firstEl = isFlightsSegments ? data[0] : data;
  const lastEl = isFlightsSegments ? data[data?.length - 1] : data;
  const isWithoutStops = isFlightsSegments ? data?.length === 1 : true;

  const stopLocationCodes = [];
  const operatedByAirlineLogos = new Set();
  const operatedAirlinesNames = new Set();
  const stopAirlineNames: any = [];

  const dataAsArr = isFlightsSegments ? data : [data];

  for (let i = 0; i < dataAsArr.length; i++) {
    stopLocationCodes.push(dataAsArr[i].to.code);
    if (!stopAirlineNames.includes(dataAsArr[i].airline.name)) {
      stopAirlineNames.push(dataAsArr[i].airline.name);
    }
    if (dataAsArr[i].operated_airline) {
      if (dataAsArr[i].operated_airline.id !== dataAsArr[i].airline.id) {
        operatedByAirlineLogos.add(dataAsArr[i].operated_airline.image);
        operatedAirlinesNames.add(dataAsArr[i].operated_airline.name);
      }
    }
  }

  const cabinSummary = summarizeCabin(dataAsArr);

  const newObj = {
    operatedBy:
      operatedAirlinesNames.size > 0
        ? Array.from(operatedAirlinesNames).join(' ; ')
        : false,
    operatedByAirlineLogos: Array.from(operatedByAirlineLogos),
    flight_number: isWithoutStops
      ? firstEl?.flight_number
      : `${firstEl?.flight_number}-${lastEl?.flight_number}`,
    flight_joint_name: isWithoutStops
      ? buildFlightJointName(firstEl?.airline?.id, firstEl?.flight_number)
      : `${buildFlightJointName(firstEl?.airline?.id, firstEl?.flight_number)} + ${buildFlightJointName(lastEl?.airline?.id, lastEl?.flight_number)}`,
    stop_location_codes: stopLocationCodes.join(' - '),
    stop_airline_names: stopAirlineNames.join(', '),
    from: firstEl?.from,
    departure_date: firstEl?.departure_date,
    baggage: firstEl?.baggage,
    num_of_stops: dataAsArr?.length - 1,
    to: lastEl?.to,
    arrival_date: lastEl?.arrival_date,
    available_seats: firstEl?.available_seats
      ? Number(firstEl?.available_seats)
      : -1,
    // Delivered cabin, collapsed across every segment in this leg.
    // null when no segment reported one — ALP/SKY360 today — so consumers
    // render no tag at all rather than an empty/placeholder one.
    cabin: cabinSummary?.cabin ?? null,
    cabinMixed: cabinSummary?.mixed ?? false,
  };

  return newObj;
};

interface Props {
  flightData: any;
  handlePurchase: (type: FlightType, f1: any) => void;
  isShowFlightButton?: boolean;
  isMinimized?: boolean;
  isCompact?: boolean;
  flightFromSubProducts?: Product[];
  flightBackSubProducts?: Product[];
  buttonText?: string;
  isPreviewMode?: boolean;
  showProductPrice?: boolean;
  isDynamicPackage?: boolean;
  isCartView?: boolean;
  calcPriceDiff?: ({
    flightPrice,
    identifier,
  }: {
    flightPrice: PriceDetails;
    identifier?: string;
  }) => PriceDetails | null;
  urlSearchParams?: any;
  paxFromUrlParams?: UrlPaxCount;
  hidePaxBreakdown?: boolean;
  /**
   * Suppresses the delivered-cabin pill — a
   * dedicated content flag, deliberately separate from `isCompact`.
   * `isCompact` is reused for styling by several surfaces
   * (`ReselectFlightListComponent`, `SearchResultsBundleFlight` — the
   * Dynamic Package "Change Flight" modal) that have no layout problem with
   * the pill at all; gating on `isCompact` once already made that modal
   * silently lose it. Only the bot chat widget
   * (`components/bot/search-results/SearchResultList.tsx`, whose narrow
   * `.airline` column genuinely clips it) sets this.
   */
  hideCabinTag?: boolean;
}

const FlightCard2 = ({
  flightData: data,
  handlePurchase,
  isShowFlightButton = true,
  isMinimized = false,
  isCompact = false,
  flightFromSubProducts,
  flightBackSubProducts,
  buttonText,
  isPreviewMode,
  showProductPrice = true,
  isDynamicPackage,
  calcPriceDiff,
  urlSearchParams,
  paxFromUrlParams,
  hidePaxBreakdown = false,
  isCartView = false,
  hideCabinTag = false,
}: Props) => {
  const { searchParams: reduxSearchParams } = useSelector((data: RootState) => data.search);
  const searchParams = urlSearchParams || reduxSearchParams;
  const { shoppingCartTranslations: t } = useSelector(
    (state: RootState) => state.config.translations
  );

  const flightData = data?.flightsInfo ?? data;
  const isSkyInventory = getTsTypeByIdentifier({identifier: flightData?.identifier, type: 'flight'}) === TenantTsType.SKY;

  const flightKey = flightData?.flightFrom ? 'flightFrom' : 'flight';

  const isRoundTrip = flightData?.[flightKey]?.[0]?.baggage?.is_round_trip;

  const isOneWayFlight =
    (!flightData?.[flightKey] && flightData?.flightBack) ||
    (flightData?.[flightKey] && !flightData?.flightBack);

  const rawFlightEntry = flightData?.[flightKey] ?? flightData?.flightBack;
  const airLine = Array.isArray(rawFlightEntry)
    ? rawFlightEntry[0]?.airline
    : rawFlightEntry?.airline;

  const flightFromShortData = useMemo(() => {
    return modifyFlightData(flightData?.[flightKey]);
  }, [flightData?.[flightKey]]);

  const flightBackShortData = useMemo(() => {
    return modifyFlightData(flightData?.flightBack);
  }, [flightData?.flightBack]);

  const reduxPax = useSelector((state: RootState) =>
    selectSearchParamsPaxCount(state, isDynamicPackage)
  );
  // Prefer pax from URL params (the counts used for the last search) so pricing
  // stays correct when the pax picker is changed before Search is clicked.
  // Dynamic packages derive pax from multi_room_guests in Redux, so keep those.
  const pax =
    !isDynamicPackage && paxFromUrlParams ? paxFromUrlParams : reduxPax;

  const isSameAirline = () => {
    // if it's not an array, it's a single flight
    if (!Array.isArray(flightData[flightKey])) {
      return (
        isOneWayFlight ||
        flightData[flightKey].airline.id === flightData.flightBack.airline.id
      );
    }
    // else if it's an array, it's a round trip

    const fromAirlines = flightData[flightKey].map(
      (flight: Flight) => flight.airline.name
    );
    if (!flightData?.flightBack) {
      return fromAirlines.every(
        (airline: string, index: number, arr: string[]) =>
          airline === arr[0]
      );
    }

    const backAirlines = flightData.flightBack.map(
      (flight: Flight) => flight.airline.name
    );

    return fromAirlines.every(
      (airline: string, index: number, arr: string[]) =>
        airline === backAirlines[index] && airline === arr[0]
    );
  };

  const calculatedPriceDiff = useMemo(() => {
    if (!calcPriceDiff) {
      return undefined;
    }
    const priceDiff = calcPriceDiff({ flightPrice: flightData.price, identifier: flightData.identifier });

    return priceDiff || undefined;
  }, [calcPriceDiff, flightData.price]);

  return (
    <div>
      <div
        className={`flight-card-2
        ${isCartView ? 'is-cart-view' : ''}
        ${isMinimized ? 'flight-card-2-minimized' : ''}
        ${isCompact ? 'flight-card-2-compact' : ''}
        ${isSkyInventory && !isCartView ? 'is-recommended-flight' : ''}
        `}
      >
        {isSkyInventory && (
          <SkyInventoryBadge
            label={t?.recommendedFlight}
            variant={isCartView ? 'inline' : 'corner'}
          />
        )}
        <Row>
          {!isMinimized && (
            <Col md={isMinimized ? 24 : 5} xs={24} className="airline-wrapper">
              {isSameAirline() ? (
                <AirlineLogoWithToolTip
                  airline={airLine}
                  isFlightExist
                  showAirlineInfoTitle={!isCompact}
                />
              ) : (
                <>
                  <AirlineLogoWithToolTip
                    airline={flightData?.[flightKey]?.[0]?.airline}
                    isFlightExist={flightData?.[flightKey]}
                    firstFlight={flightData?.[flightKey]?.[0]}
                    showAirlineInfoTitle={!isCompact}
                  />
                  {flightData?.flightBack && (
                    <AirlineLogoWithToolTip
                      airline={flightData?.flightBack?.[0]?.airline}
                      isFlightExist={flightData?.flightBack}
                      firstFlight={flightData?.flightBack?.[0]}
                      showAirlineInfoTitle={!isCompact}
                    />
                  )}
                </>
              )}
            </Col>
          )}
          <Col md={isMinimized ? 24 : 19} xs={24} className="flights-wrapper">
            {isMinimized && <div className="dotted-border-top" />}

            {isMinimized && (
              <AirlineLogoWithToolTip
                firstFlight={flightData?.[flightKey]?.[0]}
                airline={flightData?.[flightKey]?.[0]?.airline}
                isFlightExist={flightData?.[flightKey]}
              />
            )}

            {flightData?.[flightKey] && (
              <FlightOutLineSection
                flightData={flightData?.[flightKey]}
                flightOutlineData={flightFromShortData}
                flightSubProducts={flightFromSubProducts}
                isMinimized={isMinimized}
                flightIdentifier={flightData.identifier}
                showProductPrice={showProductPrice}
                hideCabinTag={hideCabinTag}
              />
            )}

            {flightData?.flightBack && <div className="dotted-border-top" />}

            {isMinimized && flightData?.flightBack && (
              <AirlineLogoWithToolTip
                airline={flightData?.flightBack?.[0]?.airline}
                isFlightExist={flightData?.flightBack}
                firstFlight={flightData?.flightBack?.[0]}
              />
            )}

            {flightBackShortData && (
              <FlightOutLineSection
                flightData={flightData?.flightBack}
                flightOutlineData={flightBackShortData}
                flightSubProducts={
                  isRoundTrip ? flightFromSubProducts : flightBackSubProducts
                }
                isMinimized={isMinimized}
                // backFlight
                flightIdentifier={flightData.identifier}
                showProductPrice={showProductPrice}
                hideCabinTag={hideCabinTag}
              />
            )}
          </Col>
        </Row>

        {!isPreviewMode && isShowFlightButton && (
          <FlightButton2
            flightPrice={calculatedPriceDiff || flightData.price}
            showPriceDifference={!!calculatedPriceDiff}
            pax={pax ?? { adult: 0, child: 0, infant: 0 }}
            handlePurchase={handlePurchase}
            f1={flightData.identifier}
            type={FlightType.BundleFlight}
            suggested_commission={
              flightData?.agent_additional_info?.suggested_commission
            }
            buttonText={buttonText}
            showProductPrice={showProductPrice}
            noAvailabilityForThisPrice={flightData?.available === false}
            isCompact={isCompact}
            showRatesTooltip={false}
            hidePaxBreakdown={hidePaxBreakdown}
          />
        )}
      </div>
      {!isMinimized && (
        <AgentCommissionSearch
          centered
          agentAdditionalInfo={flightData?.agent_additional_info}
        />
      )}
    </div>
  );
};

export default FlightCard2;
